import React, { useEffect, useState } from "react";
import { CreditCard, QrCode, X } from "lucide-react";

import { useLanguage } from "../contexts/LanguageContext";
import {
  getSubscriptionCopy,
  getSubscriptionMonthlyEquivalent,
  getSubscriptionPeriodLabel,
  getSubscriptionPrice,
  type SubscriptionBilling,
  type SubscriptionPlan,
} from "../utils/subscriptionPricing";

type PayMethod = "credit_card" | "pix";

export type SubscribeBilling = SubscriptionBilling;

export default function SubscribeModal({
  open,
  onClose,
  onOpenSubscribe,
  initialTier = "pro",
  initialBilling = "monthly",
}: {
  open: boolean;
  onClose: () => void;
  initialTier?: SubscriptionPlan;
  initialBilling?: SubscribeBilling;
  onOpenSubscribe: (params: {
    tier: SubscriptionPlan;
    interval: "monthly" | "yearly";
    method: PayMethod;
    successUrl: string;
    cancelUrl: string;
  }) => Promise<void>;
}) {
  const { language } = useLanguage();
  const copy = getSubscriptionCopy(language);
  const [tier, setTier] = useState<SubscriptionPlan>(initialTier);
  const [billing, setBilling] = useState<SubscribeBilling>(initialBilling);
  const [method, setMethod] = useState<PayMethod>(() => {
    const saved = localStorage.getItem("orthoprecision_pay_method");
    return (saved as PayMethod) || "credit_card";
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    localStorage.setItem("orthoprecision_pay_method", method);
  }, [method]);

  useEffect(() => {
    if (open) {
      setTier(initialTier);
      setBilling(initialBilling);
    }
  }, [open, initialTier, initialBilling]);

  if (!open) return null;

  const annualReady = true;
  const canCheckout = method !== "pix";

  const handleContinue = async () => {
    if (!canCheckout) return;
    try {
      setBusy(true);
      const successUrl = `${window.location.origin}/app?checkout=success`;
      const cancelUrl = `${window.location.origin}/app?checkout=cancel`;
      const interval: "monthly" | "yearly" = billing === "annual" ? "yearly" : "monthly";
      await onOpenSubscribe({ tier, interval, method, successUrl, cancelUrl });
    } finally {
      setBusy(false);
    }
  };

  const planCards: Array<{ key: SubscriptionPlan; label: string }> = [
    { key: "pro", label: "OrthoPrecision Pro" },
    { key: "premium", label: "OrthoPrecision Premium" },
  ];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl shadow-black/60">
        <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-slate-500">
              {copy.activateLicense}
            </p>
            <h3 className="mt-0.5 text-lg font-bold text-white">{copy.choosePlan}</h3>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-900 hover:text-white"
            aria-label={copy.close}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 p-5">
          <div className="flex items-center gap-1 rounded-xl border border-slate-800 bg-slate-900/60 p-1">
            <button
              onClick={() => setBilling("monthly")}
              className={[
                "flex-1 rounded-lg py-2 text-xs font-bold transition-all",
                billing === "monthly"
                  ? "bg-slate-700 text-white shadow-sm"
                  : "text-slate-400 hover:text-slate-200",
              ].join(" ")}
            >
              {copy.monthly}
            </button>
            <button
              onClick={() => (!annualReady ? undefined : setBilling("annual"))}
              className={[
                "relative flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-xs font-bold transition-all",
                billing === "annual"
                  ? "bg-slate-700 text-white shadow-sm"
                  : annualReady
                    ? "text-slate-400 hover:text-slate-200"
                    : "cursor-default text-slate-600",
              ].join(" ")}
            >
              {copy.annual}
              {annualReady ? (
                <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold text-emerald-400">
                  {copy.twoMonthsFree}
                </span>
              ) : (
                <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] text-slate-600">
                  {copy.comingSoon}
                </span>
              )}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {planCards.map(({ key, label }) => {
              const priceLabel = getSubscriptionPrice(language, key, billing);
              const period = getSubscriptionPeriodLabel(language, billing);
              const isSelected = tier === key;
              const isPro = key === "pro";
              return (
                <button
                  key={key}
                  onClick={() => setTier(key)}
                  className={[
                    "flex flex-col items-start rounded-2xl border p-4 text-left transition-all",
                    isSelected && isPro
                      ? "border-cyan-500/50 bg-cyan-500/10 shadow-[0_0_24px_-8px_rgba(34,211,238,0.2)]"
                      : isSelected && !isPro
                        ? "border-violet-500/50 bg-violet-500/10 shadow-[0_0_24px_-8px_rgba(139,92,246,0.2)]"
                        : "border-slate-700 bg-slate-900/40 hover:border-slate-600",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "text-[10px] font-bold uppercase tracking-widest",
                      isSelected && isPro
                        ? "text-cyan-400"
                        : isSelected && !isPro
                          ? "text-violet-400"
                          : "text-slate-500",
                    ].join(" ")}
                  >
                    {key.toUpperCase()}
                  </span>
                  <span className="mt-2 text-2xl font-bold tracking-tight text-white">{priceLabel}</span>
                  <span className="text-xs text-slate-400">{period}</span>
                  {billing === "annual" && (
                    <span className="mt-0.5 text-[10px] text-slate-500">
                      {getSubscriptionMonthlyEquivalent(language, key)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.24em] text-slate-500">
              {copy.paymentMethod}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setMethod("credit_card")}
                className={[
                  "flex flex-1 items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-semibold transition-all",
                  method === "credit_card"
                    ? "border-slate-600 bg-slate-800 text-white"
                    : "border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-200",
                ].join(" ")}
              >
                <CreditCard className="h-4 w-4" />
                {copy.creditCard}
              </button>
              <button
                onClick={() => setMethod("pix")}
                className={[
                  "flex flex-1 items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-semibold transition-all",
                  method === "pix"
                    ? "border-slate-600 bg-slate-800 text-white"
                    : "border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-200",
                ].join(" ")}
              >
                <QrCode className="h-4 w-4" />
                {copy.pix}
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={handleContinue}
            disabled={busy || !canCheckout}
            className={[
              "w-full rounded-xl py-3.5 text-sm font-bold shadow-lg transition-all disabled:opacity-60",
              tier === "pro"
                ? "bg-cyan-500 text-slate-950 shadow-cyan-500/20 hover:bg-cyan-400"
                : "bg-violet-600 text-white shadow-violet-500/20 hover:bg-violet-500",
            ].join(" ")}
          >
            {busy
              ? copy.openingCheckout
              : !canCheckout
                ? copy.pixComingSoon
                : copy.subscribePlan(planCards.find((item) => item.key === tier)?.label ?? tier)}
          </button>

          <button
            type="button"
            onClick={onClose}
            className="w-full py-2 text-xs font-bold uppercase tracking-widest text-slate-500 transition-colors hover:text-slate-300"
          >
            {copy.back}
          </button>

          <p className="text-center text-[11px] text-slate-600">
            {method === "pix" ? copy.pix30Days : copy.cardRecurring}
          </p>
        </div>
      </div>
    </div>
  );
}
